import { getApiUrl } from '@/utils/apiConfig';
import { create } from 'zustand';
import { useSettingsStore } from "./settingsStore";

export interface Interaction {
  id: string;
  candidateId?: string;
  candidateName: string;
  partyAcronym: string;
  senderLastName: string;
  senderFirstName: string;
  senderEmail: string;
  senderPhone: string;
  region: string;
  province: string;
  subject: string;
  message: string;
  date: string;
  status: "nouveau" | "lu" | "traite" | "pending" | "transferred" | "rejected" | "approved";
  type: "party" | "candidate" | "engagement" | "public_question";
  internalNote?: string;
  isIntercepted?: boolean;
  isPublic?: boolean;
  publicReply?: string;
  genre?: string;
  age?: string;
  profession?: string;
}

interface InteractionsStore {
  interactions: Interaction[];
  setInteractions: (interactions: Interaction[]) => void;
  isGlobalInteractionsEnabled: boolean;
  isGlobalQaEnabled: boolean;
  setGlobalInteractionsEnabled: (enabled: boolean) => void;
  setGlobalQaEnabled: (enabled: boolean) => void;
  setBlockedParties: (parties: string[]) => void;
}

export const useInteractionsStore = create<InteractionsStore>((set) => ({
  interactions: [],
  setInteractions: (interactions) => set({ interactions }),
  isGlobalInteractionsEnabled: true,
  isGlobalQaEnabled: true,
  setGlobalInteractionsEnabled: (enabled) => set({ isGlobalInteractionsEnabled: enabled }),
  setGlobalQaEnabled: (enabled) => set({ isGlobalQaEnabled: enabled }),
  setBlockedParties: (parties) => set({ blockedParties: parties })
}));

export async function fetchInteractionsFromServer(partyAcronym: string) {
  if (!partyAcronym) return;
  try {
    const res = await fetch(`${getApiUrl()}/interactions.php?party=${partyAcronym}&t=${Date.now()}`);
    if (!res.ok) throw new Error("API Error");
    const data = await res.json();
    useInteractionsStore.getState().setInteractions(data);
  } catch (e) {
    console.error("Failed to fetch interactions", e);
  }
}

export async function fetchAllInteractionsFromServer() {
  try {
    const res = await fetch(`${getApiUrl()}/interactions.php?party=all&t=${Date.now()}`);
    if (!res.ok) throw new Error("API Error");
    const data = await res.json();
    useInteractionsStore.getState().setInteractions(data);
    return data;
  } catch (e) {
    console.error("Failed to fetch all interactions", e);
    return [];
  }
}

// Fetch public QA (approved only)
export async function fetchPublicQaFromServer(partyAcronym: string): Promise<Interaction[]> {
  if (!partyAcronym) return [];
  try {
    const res = await fetch(`${getApiUrl()}/interactions.php?party=public_qa&target=${partyAcronym}&t=${Date.now()}`);
    if (!res.ok) throw new Error("API Error");
    const data = await res.json();
    return data;
  } catch (e) {
    console.error("Failed to fetch public QA", e);
    return [];
  }
}

// Retro-compatibility mock for DashboardLayout.tsx initial state
export function loadAllInteractions(): Interaction[] {
  return useInteractionsStore.getState().interactions;
}

export async function saveInteraction(interaction: Omit<Interaction, "id" | "date" | "status">) {
  const newInteraction: Interaction = {
    type: "candidate",
    ...interaction,
    id: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    date: new Date().toISOString(),
    status: "nouveau",
  };
  
  // Update state optimistically
  useInteractionsStore.getState().setInteractions([newInteraction, ...useInteractionsStore.getState().interactions]);
  
  try {
    await fetch(`${getApiUrl()}/interactions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'create', interaction: newInteraction })
    });
  } catch (e) { console.error(e); }
  
  return newInteraction;
}

export async function markInteractionAsRead(id: string) {
  const current = useInteractionsStore.getState().interactions;
  useInteractionsStore.getState().setInteractions(
    current.map(i => i.id === id ? { ...i, status: "lu" } : i)
  );
  
  try {
    await fetch(`${getApiUrl()}/interactions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update_status', id, status: 'lu' })
    });
  } catch (e) { console.error(e); }
}

export async function markInteractionAsHandled(id: string, note?: string) {
  const current = useInteractionsStore.getState().interactions;
  useInteractionsStore.getState().setInteractions(
    current.map(i => i.id === id ? { ...i, status: "traite", internalNote: note } : i)
  );
  
  try {
    await fetch(`${getApiUrl()}/interactions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update_status', id, status: 'traite', note })
    });
  } catch (e) { console.error(e); }
}

export async function deleteInteraction(id: string) {
  const current = useInteractionsStore.getState().interactions;
  useInteractionsStore.getState().setInteractions(current.filter(i => i.id !== id));
  
  try {
    await fetch(`${getApiUrl()}/interactions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id })
    });
  } catch (e) { console.error(e); }
}

export async function updateInteraction(id: string, updates: Partial<Interaction>) {
  const current = useInteractionsStore.getState().interactions;
  useInteractionsStore.getState().setInteractions(
    current.map(i => i.id === id ? { ...i, ...updates } : i)
  );

  try {
    await fetch(`${getApiUrl()}/interactions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update', id, updates })
    });
  } catch (e) { console.error(e); }
}

export function getInteractionsByParty(partyAcronym: string): Interaction[] {
  return useInteractionsStore.getState().interactions.filter(i => i.partyAcronym === partyAcronym && !i.isIntercepted);
}

// Global Interactions Settings

const GLOBAL_INTERACTIONS_KEY = "kaynin_interactions_global_enabled";
export function isGlobalInteractionsEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const val = localStorage.getItem(GLOBAL_INTERACTIONS_KEY);
  return val !== null ? val === "true" : true;
}

export function setGlobalInteractionsEnabled(enabled: boolean) {
  if (typeof window !== "undefined") {
    localStorage.setItem(GLOBAL_INTERACTIONS_KEY, String(enabled));
    updateSettingOnServer(GLOBAL_INTERACTIONS_KEY, enabled);
    window.dispatchEvent(new Event("interactionsGlobalSettingsChanged"));
  }
}

const GLOBAL_QA_KEY = "kaynin_interactions_global_qa_enabled";
export function isGlobalQaEnabled(): boolean {
  if (typeof window === "undefined") return true;
  const val = localStorage.getItem(GLOBAL_QA_KEY);
  return val !== null ? val === "true" : true;
}

export function setGlobalQaEnabled(enabled: boolean) {
  if (typeof window !== "undefined") {
    localStorage.setItem(GLOBAL_QA_KEY, String(enabled));
    updateSettingOnServer(GLOBAL_QA_KEY, enabled);
    window.dispatchEvent(new Event("interactionsGlobalQaSettingsChanged"));
  }
}

const BLOCKED_PARTIES_KEY = "kaynin_interactions_blocked_parties";
export function getBlockedPartiesForInteractions(): string[] {
  if (typeof window === "undefined") return [];
  const val = localStorage.getItem(BLOCKED_PARTIES_KEY);
  return val ? JSON.parse(val) : [];
}

export function setBlockedPartiesForInteractions(parties: string[]) {
  if (typeof window !== "undefined") {
    localStorage.setItem(BLOCKED_PARTIES_KEY, JSON.stringify(parties));
    useSettingsStore.getState().updateSetting(BLOCKED_PARTIES_KEY, JSON.stringify(parties));
    window.dispatchEvent(new Event("interactionsPartySettingsChanged"));
  }
}

export function togglePartyInteractionBlock(acronym: string) {
  const blocked = getBlockedPartiesForInteractions();
  if (blocked.includes(acronym)) {
    setBlockedPartiesForInteractions(blocked.filter(p => p !== acronym));
  } else {
    setBlockedPartiesForInteractions([...blocked, acronym]);
  }
}
